
import React, { useState, useRef } from 'react';
import { X, Upload, FileSpreadsheet, Check, Loader2, Table, ArrowRight, Layers } from 'lucide-react';
import { supabase } from '../supabase';
import { User, Contact, DocumentStatus, DocumentResult } from '../types';
import { defaultDocumentTypes } from '../data';
import * as XLSX from 'xlsx';

interface ImportDocumentsModalProps {
  onClose: () => void;
  onRefresh: () => void;
  currentUser: User;
  containerId: string;
  contacts: Contact[];
}

type ImportStep = 'idle' | 'mapping' | 'processing' | 'success';

const normalizeText = (t: any) => String(t ?? '').trim().toUpperCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

// Aceita serial do Excel, Date, dd/mm/aaaa e aaaa-mm-dd
const parseSheetDate = (val: any): string | null => {
  if (val === null || val === undefined || val === '') return null;
  if (val instanceof Date && !isNaN(val.getTime())) return val.toISOString();
  if (typeof val === 'number' && val > 20000 && val < 80000) {
    const d = new Date(Math.round((val - 25569) * 86400 * 1000));
    return isNaN(d.getTime()) ? null : d.toISOString();
  }
  const str = String(val).trim();
  const br = str.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})$/);
  if (br) {
    const year = br[3].length === 2 ? `20${br[3]}` : br[3];
    const d = new Date(Number(year), Number(br[2]) - 1, Number(br[1]), 12);
    return isNaN(d.getTime()) ? null : d.toISOString();
  }
  const iso = new Date(str);
  return isNaN(iso.getTime()) ? null : iso.toISOString();
};

const normalizeStatus = (val: any): DocumentStatus => {
  const t = normalizeText(val);
  if (!t) return DocumentStatus.FILED;
  if (t.includes('AGUARD') || t.includes('PROTOCOL')) return DocumentStatus.FILED;
  if (t.includes('RESPOND')) return DocumentStatus.ANSWERED;
  if (t.includes('ARQUIV')) return DocumentStatus.ARCHIVED;
  if (t.includes('CANCEL')) return DocumentStatus.CANCELED;
  if (t.includes('ELABOR')) return DocumentStatus.ELABORATING;
  return DocumentStatus.FILED;
};

const normalizeResult = (val: any): DocumentResult | null => {
  const t = normalizeText(val);
  if (!t) return null;
  if (t.includes('PARCIAL')) return DocumentResult.PARTIAL;
  if (t.includes('ATENDIDO')) return DocumentResult.FULFILLED;
  if (t.includes('NEGADO')) return DocumentResult.DENIED;
  if (t.includes('INVIAVEL')) return DocumentResult.UNFEASIBLE;
  if (t.includes('IGNORADO')) return DocumentResult.IGNORED;
  if (t.includes('ARQUIV')) return DocumentResult.ARCHIVED;
  return null;
};

const ImportDocumentsModal: React.FC<ImportDocumentsModalProps> = ({ onClose, onRefresh, currentUser, containerId, contacts }) => {
  const [step, setStep] = useState<ImportStep>('idle');
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [fileData, setFileData] = useState<any[][]>([]);
  const [csvRowsPreview, setCsvRowsPreview] = useState<string[][]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [importCount, setImportCount] = useState(0);
  const [createdContactsCount, setCreatedContactsCount] = useState(0);
  const [defaultType, setDefaultType] = useState(defaultDocumentTypes[0]);
  const [createMissingContacts, setCreateMissingContacts] = useState(true);

  const [mapping, setMapping] = useState({
    document_number: '',
    date: '',
    received_by: '',
    internal_requester: '',
    requester: '',
    address_street: '',
    theme: '',
    subject: '',
    recipient: '',
    status: '',
    resultado: '',
    response_date: '',
    response_text: ''
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    setErrorMsg(null);
    try {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = new Uint8Array(event.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array', cellDates: true });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
          if (jsonData.length < 2) throw new Error('Arquivo insuficiente.');
          const headers = (jsonData[0] as any[]).map(h => String(h || '').trim());
          setCsvHeaders(headers);
          setFileData(jsonData.slice(1));
          setCsvRowsPreview(jsonData.slice(1, 6).map(row => headers.map((_, j) => {
            const cell = row[j];
            return cell instanceof Date ? cell.toLocaleDateString('pt-BR') : String(cell ?? '');
          })));
          setStep('mapping');
          setMapping({
            document_number: headers.find(h => /of[ií]cio|n[uú]mero|identifica/i.test(h)) || '',
            date: headers.find(h => /protocolo|data_prot|^data$/i.test(h)) || '',
            received_by: headers.find(h => /entrada/i.test(h)) || '',
            internal_requester: headers.find(h => /requerente/i.test(h)) || '',
            requester: headers.find(h => /lideran/i.test(h)) || '',
            address_street: headers.find(h => /local|endere/i.test(h)) || '',
            theme: headers.find(h => /tema/i.test(h)) || '',
            subject: headers.find(h => /assunto|ementa/i.test(h)) || '',
            recipient: headers.find(h => /destinat/i.test(h)) || '',
            status: headers.find(h => /status|situa/i.test(h)) || '',
            resultado: headers.find(h => /efetividade|resultado/i.test(h)) || '',
            response_date: headers.find(h => /devolutiva/i.test(h)) || '',
            response_text: headers.find(h => /retorno/i.test(h)) || ''
          });
        } catch (err) { setErrorMsg('Erro ao ler arquivo.'); } finally { setLoading(false); }
      };
      reader.readAsArrayBuffer(file);
    } catch (err) { setErrorMsg('Falha no carregamento.'); setLoading(false); }
  };

  const performImport = async () => {
    if (!mapping.subject) { setErrorMsg('A coluna de Assunto / Ementa é obrigatória.'); return; }
    if (!containerId || containerId === 'undefined' || containerId === 'null') {
      setErrorMsg('Gabinete não identificado. Recarregue a página (F5) e tente novamente.');
      return;
    }
    setErrorMsg(null);
    setLoading(true);
    setStep('processing');
    try {
      const col = (key: keyof typeof mapping) => mapping[key] ? csvHeaders.indexOf(mapping[key]) : -1;
      const idx = {
        document_number: col('document_number'), date: col('date'), received_by: col('received_by'),
        internal_requester: col('internal_requester'), requester: col('requester'), address_street: col('address_street'),
        theme: col('theme'), subject: col('subject'), recipient: col('recipient'), status: col('status'),
        resultado: col('resultado'), response_date: col('response_date'), response_text: col('response_text')
      };
      const cell = (row: any[], i: number) => i !== -1 && row[i] !== undefined && row[i] !== null ? String(row[i] instanceof Date ? row[i].toISOString() : row[i]).trim() : '';

      const rows = fileData.filter(row => idx.subject !== -1 && String(row[idx.subject] ?? '').trim());

      // Vincula Liderança ao CRM por nome; cria contatos ausentes se habilitado
      const contactByName = new Map<string, string>();
      contacts.forEach(c => contactByName.set(normalizeText(c.name), c.id));
      let newContacts = 0;
      if (idx.requester !== -1) {
        const missing = new Set<string>();
        rows.forEach(row => {
          const name = cell(row, idx.requester);
          if (name && !contactByName.has(normalizeText(name)) && normalizeText(name) !== 'INTERNO') missing.add(name);
        });
        if (createMissingContacts && missing.size > 0) {
          const payload = Array.from(missing).map(name => ({
            container_id: containerId, name, type: 'Liderança Regional', phones: [], emails: [],
            city: '', neighborhood: '', tags: ['Importado'], observations: 'Criado automaticamente na importação de documentos.',
            created_by: currentUser.id
          }));
          const { data, error } = await supabase.from('contacts').insert(payload).select('id, name');
          if (error) throw new Error(`Falha ao criar contatos das lideranças: ${error.message}`);
          (data || []).forEach((c: any) => contactByName.set(normalizeText(c.name), c.id));
          newContacts = data?.length || 0;
        }
      }
      setCreatedContactsCount(newContacts);

      // Cadastra temas ainda não existentes no gabinete
      if (idx.theme !== -1) {
        const sheetThemes = new Set<string>();
        rows.forEach(row => { const t = cell(row, idx.theme); if (t) sheetThemes.add(t); });
        if (sheetThemes.size > 0) {
          const { data: existing } = await supabase.from('document_themes').select('name').eq('container_id', containerId);
          const known = new Set((existing || []).map((t: any) => normalizeText(t.name)));
          const toCreate = Array.from(sheetThemes).filter(t => !known.has(normalizeText(t)));
          if (toCreate.length > 0) {
            await supabase.from('document_themes').insert(toCreate.map(name => ({ container_id: containerId, name })));
          }
        }
      }

      const documents = rows.map(row => {
        const requesterName = cell(row, idx.requester);
        return {
          container_id: containerId,
          subject: cell(row, idx.subject),
          document_type: defaultType,
          document_number: cell(row, idx.document_number),
          recipient: cell(row, idx.recipient),
          requester_type: 'contact',
          requester_id: requesterName ? (contactByName.get(normalizeText(requesterName)) || null) : null,
          responsible_id: currentUser.id,
          made_by_id: currentUser.id,
          date: (idx.date !== -1 ? parseSheetDate(row[idx.date]) : null) || new Date().toISOString(),
          status: normalizeStatus(cell(row, idx.status)),
          theme: cell(row, idx.theme) || null,
          resultado: idx.resultado !== -1 ? normalizeResult(cell(row, idx.resultado)) : null,
          received_by: cell(row, idx.received_by) || null,
          internal_requester: cell(row, idx.internal_requester) || null,
          address_street: cell(row, idx.address_street) || null,
          response_date: idx.response_date !== -1 ? parseSheetDate(row[idx.response_date]) : null,
          response_text: cell(row, idx.response_text) || null,
          history: [{ id: `h-doc-imp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, userId: currentUser.id, timestamp: new Date().toISOString(), action: 'Importado via planilha' }]
        };
      });

      let inserted = 0;
      for (let i = 0; i < documents.length; i += 200) {
        const chunk = documents.slice(i, i + 200);
        const { error } = await supabase.from('documents').insert(chunk);
        if (error) throw new Error(`Falha ao importar documentos: ${error.message}`);
        inserted += chunk.length;
      }
      setImportCount(inserted);
      setStep('success');
    } catch (err: any) {
      setErrorMsg(err.message);
      setStep('mapping');
    } finally { setLoading(false); }
  };

  const mappingFields: { key: keyof typeof mapping; label: string; hint?: string }[] = [
    { key: 'document_number', label: 'Nº DO OFÍCIO (IDENTIFICAÇÃO)' },
    { key: 'date', label: 'DATA DO PROTOCOLO' },
    { key: 'received_by', label: 'ENTRADA NO GABINETE', hint: 'Assessor que recebeu o pedido' },
    { key: 'internal_requester', label: 'REQUERENTE INTERNO', hint: 'Funcionário do gabinete que pediu' },
    { key: 'requester', label: 'LIDERANÇA (CONTATO CRM)', hint: 'Vinculado por nome ao CRM' },
    { key: 'address_street', label: 'LOCAL (ENDEREÇO / RUA)' },
    { key: 'theme', label: 'TEMA' },
    { key: 'subject', label: 'ASSUNTO / EMENTA *' },
    { key: 'recipient', label: 'DESTINATÁRIO' },
    { key: 'status', label: 'STATUS DO PROCESSO' },
    { key: 'resultado', label: 'EFETIVIDADE POLÍTICA' },
    { key: 'response_date', label: 'DATA DA DEVOLUTIVA' },
    { key: 'response_text', label: 'RETORNO OBTIDO' }
  ];

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-6 bg-navy-950/70 backdrop-blur-md animate-in fade-in" onClick={onClose}>
      <div className="bg-white dark:bg-navy-900 w-full max-w-6xl max-h-[95vh] rounded-[40px] shadow-2xl flex flex-col overflow-hidden border border-neutral-200 dark:border-navy-800 relative" onClick={e => e.stopPropagation()}>

        <div className="px-10 py-8 border-b border-slate-100 dark:border-navy-800 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-4">
            <div className="p-4 rounded-2xl bg-parliament-600 text-white shadow-lg"><FileSpreadsheet size={28} /></div>
            <div>
              <h2 className="text-2xl font-black text-slate-800 dark:text-white tracking-tight">Importação de Documentos</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Cruze as colunas da sua planilha com os campos do sistema</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-3 hover:bg-neutral-100 dark:hover:bg-navy-800 rounded-full text-neutral-400 transition-colors"><X size={28} /></button>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar p-10 bg-slate-50/30 dark:bg-navy-950/20">

          {step === 'idle' && (
            <div className="h-full flex flex-col items-center justify-center py-20 space-y-8">
              <div className="w-24 h-24 bg-parliament-50 dark:bg-navy-800 text-parliament-600 rounded-[32px] flex items-center justify-center shadow-inner">
                {loading ? <Loader2 className="animate-spin" size={40} /> : <Upload size={40} />}
              </div>
              <div className="text-center">
                <h3 className="text-2xl font-black text-navy-950 dark:text-white mb-2">Selecione sua planilha</h3>
                <p className="text-sm font-medium text-slate-500">Formatos aceitos: XLSX, XLS ou CSV. A primeira linha deve conter os nomes das colunas.</p>
              </div>
              <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept=".xlsx, .xls, .csv" className="hidden" />
              <button onClick={() => fileInputRef.current?.click()} className="bg-navy-950 dark:bg-parliament-600 text-white px-12 py-5 rounded-3xl font-black text-xs uppercase tracking-[0.2em] shadow-2xl hover:scale-105 active:scale-95 transition-all">ESCOLHER ARQUIVO</button>
              {errorMsg && <p className="text-red-500 font-bold text-xs bg-red-50 px-4 py-2 rounded-xl border border-red-100">{errorMsg}</p>}
            </div>
          )}

          {step === 'mapping' && (
            <div className="space-y-10 animate-in slide-in-from-bottom-4">
              {errorMsg && <p className="text-red-500 font-bold text-xs bg-red-50 px-4 py-3 rounded-xl border border-red-100">{errorMsg}</p>}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div className="space-y-6">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2"><Layers size={14}/> Vínculo de Dados</p>
                  <div className="bg-white dark:bg-navy-900 p-8 rounded-[32px] border border-neutral-100 dark:border-navy-700 shadow-sm space-y-4">
                    <div className="space-y-1.5">
                      <label className="text-[9px] font-black text-neutral-400 uppercase tracking-widest ml-1">TIPO DE DOCUMENTO (APLICADO A TODOS)</label>
                      <select value={defaultType} onChange={e => setDefaultType(e.target.value)} className="w-full bg-slate-50 dark:bg-navy-900 border border-neutral-100 dark:border-navy-700 rounded-xl px-4 py-3 text-xs font-bold dark:text-white outline-none">
                        {defaultDocumentTypes.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    {mappingFields.map(field => (
                      <div key={field.key} className="space-y-1.5">
                        <label className="text-[9px] font-black text-neutral-400 uppercase tracking-widest ml-1">
                          {field.label}{field.hint && <span className="normal-case font-bold text-neutral-300 tracking-normal ml-2">{field.hint}</span>}
                        </label>
                        <select value={mapping[field.key]} onChange={e => setMapping({...mapping, [field.key]: e.target.value})} className="w-full bg-slate-50 dark:bg-navy-900 border border-neutral-100 dark:border-navy-700 rounded-xl px-4 py-3 text-xs font-bold dark:text-white outline-none">
                          <option value="">Não importar / Ignorar</option>
                          {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                        </select>
                      </div>
                    ))}
                    <label className="flex items-center gap-3 p-4 bg-emerald-50 dark:bg-emerald-900/10 rounded-xl border border-emerald-100 dark:border-emerald-900/30 cursor-pointer">
                      <input type="checkbox" checked={createMissingContacts} onChange={e => setCreateMissingContacts(e.target.checked)} className="rounded" />
                      <div>
                        <span className="text-xs font-bold text-emerald-800 dark:text-emerald-300">Criar lideranças que não existem no CRM</span>
                        <p className="text-[10px] text-emerald-600">Nomes da coluna Liderança sem correspondência viram novos contatos (tag "Importado")</p>
                      </div>
                    </label>
                  </div>
                </div>
                <div className="space-y-6">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2"><Table size={14}/> Prévia da Planilha</p>
                  <div className="bg-white dark:bg-navy-800 rounded-[32px] border border-neutral-100 dark:border-navy-700 shadow-sm overflow-hidden overflow-x-auto h-[600px] custom-scrollbar">
                    <table className="w-full text-left text-[10px]">
                      <thead><tr className="bg-slate-50 dark:bg-navy-950 sticky top-0">{csvHeaders.map(h => <th key={h} className="px-4 py-3 font-black text-slate-400 uppercase whitespace-nowrap">{h}</th>)}</tr></thead>
                      <tbody className="divide-y divide-slate-50 dark:divide-navy-700">{csvRowsPreview.map((row, i) => (<tr key={i}>{row.map((cellVal, j) => <td key={j} className="px-4 py-3 font-medium text-slate-600 dark:text-slate-400 truncate max-w-[120px]">{cellVal}</td>)}</tr>))}</tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div className="flex justify-end pt-10">
                <button onClick={performImport} disabled={loading} className="bg-navy-950 dark:bg-parliament-600 text-white px-12 py-5 rounded-3xl font-black text-xs uppercase tracking-[0.2em] shadow-2xl flex items-center gap-3 hover:scale-105 transition-all disabled:opacity-50">
                  IMPORTAR {fileData.length} LINHAS <ArrowRight size={18}/>
                </button>
              </div>
            </div>
          )}

          {step === 'processing' && (
            <div className="h-full flex flex-col items-center justify-center py-20 space-y-6">
              <Loader2 className="animate-spin text-parliament-600" size={64} />
              <h3 className="text-2xl font-black text-navy-950 dark:text-white">Importando Documentos...</h3>
              <p className="text-slate-500 font-medium">Vinculando lideranças, temas e registrando os expedientes. Não feche esta janela.</p>
            </div>
          )}

          {step === 'success' && (
            <div className="h-full flex flex-col items-center justify-center py-20 space-y-8 animate-in zoom-in-95">
              <div className="w-24 h-24 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 rounded-[32px] flex items-center justify-center shadow-inner"><Check size={48} strokeWidth={3} /></div>
              <div className="text-center">
                <h3 className="text-3xl font-black text-navy-950 dark:text-white mb-2 tracking-tighter">Importação Concluída</h3>
                <p className="text-slate-500 font-medium text-lg leading-relaxed max-w-sm mx-auto">
                  {importCount} documentos importados{createdContactsCount > 0 ? ` e ${createdContactsCount} lideranças criadas no CRM` : ''}.
                </p>
              </div>
              <button onClick={() => { onRefresh(); onClose(); }} className="bg-navy-950 dark:bg-parliament-600 text-white px-12 py-5 rounded-3xl font-black text-xs uppercase tracking-[0.2em] shadow-xl transition-all hover:scale-105">VOLTAR PARA DOCUMENTOS</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportDocumentsModal;
